import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useColorMode } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import useUserQuery from '../hooks/queries/useUserQuery';
import { useBettieSettingInfo } from '../hooks/useBettieSettingInfo';
import { useGetBettieInfo } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieInfo';
import BettiePersonaSuggestion from './components/BettiePersonaSuggestion';
import BettieLoading from './components/BettieLoading';
import BettieProfileImage from './components/BettieProfileImage';
import BettieIntroPreview from './components/BettieIntroPreview';
import BettieUserNickname from './components/BettieUserNickname';
import BettieSettingComplete from './components/BettieSettingComplete';
import StickyHeader from '../components/bar/StickyHeader';
import BettieMoodSuggestion from './components/BettieMoodSuggestion';
import type { components } from '@pocapan/shared/types/schema';

export type BettieSettingStep =
  | 'room_intro'
  | 'persona_suggestion'
  | 'loading'
  | 'profile_image'
  | 'user_nickname'
  | 'mood_suggestion'
  | 'complete';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

const BettieSettingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize state from location.state if available
  const getInitialState = () => {
    const state = location.state as
      | {
          initialStep?: BettieSettingStep;
          bettiePersona?: ApiV1MemberPersona;
          chatRoomUuid?: string;
          bettieId?: string;
        }
      | undefined;
    return {
      step: state?.initialStep ?? 'room_intro',
      persona: state?.bettiePersona ?? null,
      uuid: state?.chatRoomUuid ?? '',
      bettieId: state?.bettieId ?? ''
    };
  };

  const initialState = getInitialState();
  const [currentStep, setCurrentStep] = useState<BettieSettingStep>(initialState.step);
  const [bettiePersona, setBettiePersona] = useState<ApiV1MemberPersona | null>(
    initialState.persona
  );
  const [chatRoomUuid, setChatRoomUuid] = useState<string>(initialState.uuid);
  const { bettieSettingInfo, updateBettieSettingInfo, resetBettieSettingInfo } =
    useBettieSettingInfo();
  const [stepHistory, setStepHistory] = useState<BettieSettingStep[]>([]);

  // Get bettieId from location.state or bettieSettingInfo
  const bettieId = initialState.bettieId || bettieSettingInfo.memberPersonaId;

  // Fetch bettiePersona if bettieId is available and persona is not set
  const shouldFetchBettieInfo =
    !!bettieId &&
    !bettiePersona &&
    (currentStep === 'mood_suggestion' || currentStep === 'complete');
  const { data: bettieInfoData } = useGetBettieInfo(shouldFetchBettieInfo ? bettieId : '');

  const { colorMode } = useColorMode();

  // Allow external navigation to set initial step and persona
  useEffect(() => {
    const state = location.state as
      | {
          initialStep?: BettieSettingStep;
          bettiePersona?: ApiV1MemberPersona;
          chatRoomUuid?: string;
          bettieId?: string;
        }
      | undefined;
    if (state?.initialStep) {
      setCurrentStep(state.initialStep);
    }
    if (state?.bettiePersona) {
      setBettiePersona(state.bettiePersona);
    }
    if (state?.chatRoomUuid) {
      setChatRoomUuid(state.chatRoomUuid);
    }
  }, [location.state]);

  // Set bettiePersona from bettieInfoData when available
  useEffect(() => {
    if (bettieInfoData && !bettiePersona) {
      setBettiePersona(bettieInfoData);
    }
  }, [bettieInfoData, bettiePersona]);

  useEffect(() => {
    return () => resetBettieSettingInfo();
  }, [resetBettieSettingInfo]);

  const goToPreviousStep = (nextStep: BettieSettingStep) => {
    setStepHistory((prev) => [...prev, currentStep]);
    setCurrentStep(nextStep);
  };

  const handleBackClick = () => {
    if (currentStep === 'profile_image') {
      return;
    }

    if (currentStep === 'complete') {
      navigate('/bettie-chat-list', { replace: true });
      return;
    }
    if (stepHistory.length > 0) {
      const prevStep = stepHistory[stepHistory.length - 1];
      setStepHistory((prev) => prev.slice(0, -1));
      setCurrentStep(prevStep);
      return;
    }
    navigate(-1);
  };

  const { data: user } = useUserQuery();
  const isForeignUser = user?.locale !== 'ko';

  return (
    <div
      className={`min-h-screen pt-[calc(env(safe-area-inset-top))] [@media(max-height:620px)]:pb-[calc(env(safe-area-inset-bottom)+100px)] pb-[env(safe-area-inset-bottom)] ${currentStep === 'profile_image' ? 'bg-pure-black' : 'bg-background-light dark:bg-background-dark'}`}
    >
      <Helmet>
        <title>Bettie</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#222222' : '#FFFFFF'} />
      </Helmet>
      {currentStep !== 'profile_image' && currentStep !== 'mood_suggestion' && (
        <StickyHeader
          showBackButton={true}
          showTitle={false}
          alwaysShowTitle={false}
          onClickBackButton={handleBackClick}
        />
      )}
      {currentStep === 'room_intro' && (
        <BettieIntroPreview setCurrentStep={goToPreviousStep} setBettiePersona={setBettiePersona} />
      )}
      {currentStep === 'persona_suggestion' && (
        <BettiePersonaSuggestion
          bettieSettingInfo={bettieSettingInfo}
          updateBettieSettingInfo={updateBettieSettingInfo}
          setCurrentStep={goToPreviousStep}
          setBettiePersona={setBettiePersona}
        />
      )}
      <div className="relative">
        <AnimatePresence>
          {currentStep === 'loading' && (
            <motion.div
              key="loading"
              className="absolute inset-0"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            >
              <BettieLoading
                bettieName={bettieSettingInfo.bettieNickname}
                bettiePersona={bettiePersona as ApiV1MemberPersona}
                setCurrentStep={goToPreviousStep}
                isForeignUser={isForeignUser}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {currentStep === 'profile_image' && (
            <motion.div
              key="profile_image"
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            >
              <BettieProfileImage
                bettieSettingInfo={bettieSettingInfo}
                updateBettieSettingInfo={updateBettieSettingInfo}
                setCurrentStep={goToPreviousStep}
                setStepHistory={setStepHistory}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {currentStep === 'user_nickname' && (
        <BettieUserNickname
          bettiePersona={bettiePersona as ApiV1MemberPersona}
          defaultUserNickname={user?.baseUserProfile?.nickname ?? ''}
          setChatRoomUuid={setChatRoomUuid}
          setCurrentStep={goToPreviousStep}
          isForeignUser={isForeignUser}
        />
      )}
      {currentStep === 'mood_suggestion' && bettiePersona && (
        <BettieMoodSuggestion
          setCurrentStep={setCurrentStep}
          bettiePersona={bettiePersona}
          chatRoomUuid={chatRoomUuid}
          isForeignUser={isForeignUser}
        />
      )}
      {currentStep === 'complete' && (
        <BettieSettingComplete
          bettiePersona={bettiePersona as ApiV1MemberPersona}
          bettieSettingInfo={bettieSettingInfo}
          chatRoomUuid={chatRoomUuid}
        />
      )}
    </div>
  );
};

export default BettieSettingPage;
